import { useRef, useState, useEffect } from "react";
import { api } from "../lib/api";
import { Bookmark, Plus, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Add-to-shortlist button for scouts/coaches.
 * Props:
 *  - playerId: id of the player to shortlist
 *  - shortlists: array of {id, name, player_ids} owned by the viewer
 *  - onChange: called with the updated shortlists array after any mutation
 */
export default function ShortlistButton({ playerId, shortlists, onChange }) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const inAny = shortlists.some((s) => (s.player_ids || []).includes(playerId));

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const toggle = async (s) => {
    const has = (s.player_ids || []).includes(playerId);
    setBusy(true);
    try {
      if (has) {
        await api.delete(`/shortlists/${s.id}/players/${playerId}`);
      } else {
        await api.post(`/shortlists/${s.id}/players/${playerId}`);
      }
      onChange(
        shortlists.map((x) =>
          x.id === s.id
            ? {
                ...x,
                player_ids: has
                  ? (x.player_ids || []).filter((p) => p !== playerId)
                  : [...(x.player_ids || []), playerId],
              }
            : x
        )
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update shortlist");
    } finally {
      setBusy(false);
    }
  };

  const createAndAdd = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const { data } = await api.post("/shortlists", { name });
      await api.post(`/shortlists/${data.id}/players/${playerId}`);
      onChange([{ ...data, player_ids: [playerId] }, ...shortlists]);
      setNewName("");
      toast.success(`Added to "${name}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create shortlist");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid={`shortlist-btn-${playerId}`}
        onClick={() => setOpen((o) => !o)}
        title={inAny ? "In your shortlists" : "Add to shortlist"}
        className={`px-3 py-2 border rounded-sm transition-colors ${
          inAny
            ? "border-white bg-white text-black"
            : "border-[#404040] text-[#a3a3a3] hover:text-white hover:bg-[#1a1a1a]"
        }`}
      >
        <Bookmark className={`w-4 h-4 ${inAny ? "fill-black" : ""}`} />
      </button>
      {open && (
        <div
          data-testid={`shortlist-menu-${playerId}`}
          className="absolute right-0 top-full mt-1 z-50 w-64 bg-[#121212] border border-[#404040] rounded-sm shadow-2xl p-2 space-y-1"
        >
          <div className="text-[10px] uppercase tracking-widest text-[#737373] px-2 py-1">
            Save to shortlist
          </div>
          {shortlists.length === 0 && (
            <div className="text-xs text-[#737373] px-2 py-1">No shortlists yet — create one below.</div>
          )}
          <div className="max-h-48 overflow-y-auto">
            {shortlists.map((s) => {
              const has = (s.player_ids || []).includes(playerId);
              return (
                <button
                  key={s.id}
                  data-testid={`shortlist-toggle-${s.id}`}
                  disabled={busy}
                  onClick={() => toggle(s)}
                  className="w-full flex items-center gap-2 px-2 py-2 text-sm text-left text-white hover:bg-[#1a1a1a] rounded-sm transition-colors disabled:opacity-60"
                >
                  <span
                    className={`w-4 h-4 border rounded-sm flex items-center justify-center shrink-0 ${
                      has ? "bg-white border-white" : "border-[#525252]"
                    }`}
                  >
                    {has && <Check className="w-3 h-3 text-black" />}
                  </span>
                  <span className="truncate">{s.name}</span>
                  <span className="ml-auto text-[10px] text-[#737373]">{(s.player_ids || []).length}</span>
                </button>
              );
            })}
          </div>
          <form onSubmit={createAndAdd} className="flex gap-1 pt-1 border-t border-[#262626]">
            <input
              data-testid="shortlist-new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New shortlist…"
              maxLength={60}
              className="flex-1 min-w-0 bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-2 py-1.5 rounded-sm text-sm text-white transition-colors"
            />
            <button
              data-testid="shortlist-create-btn"
              disabled={busy || !newName.trim()}
              className="px-2 py-1.5 bg-white text-black rounded-sm hover:bg-gray-200 disabled:opacity-50 transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
